import React, { useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Button } from "@/shared/components/ui/button";
import { Plus, MoreHorizontal, FolderPlus, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { DocumentType, DocumentMetadata } from "@/shared/types/documentTypes";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { replaceBucketContent } from "@/shared/utils/templateReplacement";
import DocumentVaultTable from "./DocumentVaultTable";
import { Input } from "@/shared/components/ui/input";
import { UploadDocumentModal } from "@/shared/components/overlay/UploadDocumentModal";
import { MobileUploadModal } from "@/shared/components/overlay/MobileUploadModal";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { uploadDocument, DocumentLimitError } from "@/shared/utils/uploadDocument";
import { UploadLimitModal } from "./UploadLimitModal";
import { FolderNameDialog } from "./FolderNameDialog";
import { useCreateFolder, useDeleteFolder, useRenameFolder, useMoveDocument } from "@/shared/hooks/useDocumentFolders";
import { downloadDocumentsAsZip } from "@/shared/utils/downloadDocuments";
import { enqueueSnackbar } from "notistack";

import type { DocumentFolder } from "@/shared/types/folderTypes";

interface DocumentVaultListProps {
  documents: (DocumentMetadata & {
    uploaded_by_user_id: number;
    uploaded_by_name: string;
    display_name: string | null;
  })[];
  allDocuments?: (DocumentMetadata & {
    uploaded_by_user_id: number;
    uploaded_by_name: string;
    display_name: string | null;
  })[];
  isLoading: boolean;
  error?: Error | null;
  onUploadClick?: () => boolean | void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedDocumentType: DocumentType | "all";
  onDocumentTypeChange: (type: DocumentType | "all") => void;
  remainingSlots?: number;
  onLimitReached?: () => void;
  folders?: DocumentFolder[];
  activeFolderId?: number | null;
  activeFolder?: DocumentFolder | null;
  onFolderOpen?: (folderId: number) => void;
  onFolderClose?: () => void;
}

const DocumentVaultList: React.FC<DocumentVaultListProps> = ({
  documents,
  allDocuments,
  isLoading,
  error,
  onUploadClick,
  searchQuery: _searchQuery,
  onSearchChange,
  selectedDocumentType,
  onDocumentTypeChange,
  remainingSlots,
  onLimitReached,
  folders = [],
  activeFolderId,
  activeFolder,
  onFolderOpen,
  onFolderClose,
}) => {
  const deceasedUserData = useDeceasedUserData();
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  const [folderDialogOpen, setFolderDialogOpen] = React.useState(false);
  const [folderDialogMode, setFolderDialogMode] = React.useState<"create" | "rename">("create");
  const [renamingFolder, setRenamingFolder] = React.useState<DocumentFolder | null>(null);
  const createFolderMutation = useCreateFolder();
  const renameFolderMutation = useRenameFolder();
  const deleteFolderMutation = useDeleteFolder();
  const moveDocMutation = useMoveDocument();
  const [pendingMoveDocId, setPendingMoveDocId] = React.useState<number | null>(null);

  const existingFolderNames = useMemo(
    () => folders.map((f) => f.name),
    [folders],
  );

  const handleNewFolder = () => {
    setFolderDialogMode("create");
    setRenamingFolder(null);
    setPendingMoveDocId(null);
    setFolderDialogOpen(true);
  };

  const handleNewFolderAndMove = (documentId: number) => {
    setFolderDialogMode("create");
    setRenamingFolder(null);
    setPendingMoveDocId(documentId);
    setFolderDialogOpen(true);
  };

  const handleRenameFolder = (folder: DocumentFolder) => {
    setFolderDialogMode("rename");
    setRenamingFolder(folder);
    setFolderDialogOpen(true);
  };

  const handleFolderDialogSubmit = async (name: string) => {
    try {
      if (folderDialogMode === "create") {
        const result = await createFolderMutation.mutateAsync({ name });
        if (pendingMoveDocId) {
          const folder = result.folder;
          await moveDocMutation.mutateAsync({ documentId: pendingMoveDocId, folderId: folder.id });
          enqueueSnackbar(`Moved to ${name}`, { variant: "success", autoHideDuration: 3000 });
          setPendingMoveDocId(null);
        } else {
          enqueueSnackbar("Folder created", { variant: "success", autoHideDuration: 3000 });
        }
      } else if (renamingFolder) {
        await renameFolderMutation.mutateAsync({ folderId: renamingFolder.id, name });
        enqueueSnackbar("Folder renamed", { variant: "success", autoHideDuration: 3000 });
      }
      setFolderDialogOpen(false);
    } catch {
      enqueueSnackbar("Couldn't save folder. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  const handleDeleteFolder = async (folder: DocumentFolder) => {
    try {
      const result = await deleteFolderMutation.mutateAsync(folder.id);
      const count = result.ungrouped_count ?? 0;
      enqueueSnackbar(
        `Folder deleted. ${count} ${count === 1 ? "file" : "files"} moved back to main list.`,
        { variant: "success", autoHideDuration: 3000 },
      );
      if (activeFolderId === folder.id) {
        onFolderClose?.();
      }
    } catch {
      enqueueSnackbar("Couldn't delete folder. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  const handleFolderDownload = async (folder: DocumentFolder) => {
    if (!person?.estate_id) return;
    const folderDocs = (allDocuments || documents).filter((d) => d.folder_id === folder.id);
    if (folderDocs.length === 0) {
      enqueueSnackbar("No files in this folder", { variant: "info", autoHideDuration: 3000 });
      return;
    }
    try {
      await downloadDocumentsAsZip(person.estate_id, folderDocs, folder.name);
    } catch {
      enqueueSnackbar("Couldn't download folder. Please try again.", { variant: "error", autoHideDuration: 5000 });
    }
  };

  const handleDownloadEverything = async () => {
    if (!person?.estate_id) return;
    const allDocs = allDocuments || documents;
    if (allDocs.length === 0) {
      enqueueSnackbar("No documents to download", { variant: "info", autoHideDuration: 3000 });
      return;
    }
    try {
      await downloadDocumentsAsZip(person.estate_id, allDocs, "Documents");
    } catch {
      enqueueSnackbar("Couldn't download documents. Please try again.", { variant: "error", autoHideDuration: 5000 });
    }
  };

  const handleUploadSuccess = useCallback(
    async (documentIds: number[]) => {
      if (person?.estate_id) {
        // If inside a folder, move newly uploaded docs into it
        if (activeFolderId && documentIds.length > 0) {
          await Promise.allSettled(
            documentIds.map((docId) =>
              moveDocMutation.mutateAsync({ documentId: docId, folderId: activeFolderId }),
            ),
          );
        }
        queryClient.invalidateQueries({ queryKey: ["documents", person.estate_id] });
        queryClient.invalidateQueries({ queryKey: ["document-folders", person.estate_id] });
        queryClient.invalidateQueries({ queryKey: ["hasDeathCert", person.estate_id] });
      }
    },
    [person?.estate_id, queryClient, activeFolderId, moveDocMutation],
  );
  const [docSearchParams, setDocSearchParams] = useSearchParams();
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);

  const uploadFromParam = useRef(false);
  useEffect(() => {
    if (uploadFromParam.current) return;
    if (docSearchParams.get("upload") === "true") {
      uploadFromParam.current = true;
      const next = new URLSearchParams(docSearchParams);
      next.delete("upload");
      setDocSearchParams(next, { replace: true });
      if (onUploadClick) {
        const result = onUploadClick();
        if (result === false) return;
      }
      setIsUploadModalOpen(true);
    }
  }, [docSearchParams, setDocSearchParams, onUploadClick]);
  const [isMobileUploadOpen, setIsMobileUploadOpen] = React.useState(false);
  const [limitModalFiles, setLimitModalFiles] = React.useState<{
    accepted: File[];
    blocked: File[];
  } | null>(null);
  const [isLimitUploading, setIsLimitUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  const handleLimitExceeded = (accepted: File[], blocked: File[]) => {
    setIsUploadModalOpen(false);
    setUploadError(null);
    setLimitModalFiles({ accepted, blocked });
  };

  const handleUploadAccepted = React.useCallback(
    async (filesToUpload: File[]) => {
      if (!person?.estate_id || filesToUpload.length === 0) return;
      setIsLimitUploading(true);
      const failed: string[] = [];
      const uploadedIds: number[] = [];
      for (const file of filesToUpload) {
        try {
          const docId = await uploadDocument(file, "other", person.estate_id);
          uploadedIds.push(docId);
        } catch (err) {
          if (err instanceof DocumentLimitError) {
            setUploadError((err as Error).message);
            break;
          }
          failed.push(file.name);
        }
      }
      if (activeFolderId && uploadedIds.length > 0) {
        await Promise.allSettled(
          uploadedIds.map((docId) =>
            moveDocMutation.mutateAsync({ documentId: docId, folderId: activeFolderId }),
          ),
        );
      }
      setIsLimitUploading(false);
      setLimitModalFiles(null);
      if (failed.length > 0) {
        setUploadError(
          failed.length === filesToUpload.length
            ? "Failed to upload. Please try again."
            : `Failed to upload: ${failed.join(", ")}`,
        );
      }
      if (person.estate_id) {
        queryClient.invalidateQueries({ queryKey: ["documents", person.estate_id] });
        queryClient.invalidateQueries({ queryKey: ["document-folders", person.estate_id] });
      }
    },
    [person?.estate_id, activeFolderId, moveDocMutation, queryClient],
  );

  // Document types to exclude from the filter dropdown (add types here to hide them)
  const EXCLUDED_DOCUMENT_TYPES: Set<DocumentType> = new Set([
    // Currently no exclusions - all document types from user's vault are shown
  ]);

  // Show document types that exist in user's vault, minus any excluded types
  const filteredDocumentTypes = useMemo(() => {
    const docsForTypes = allDocuments || documents;
    // Get unique document types from the user's documents
    const uniqueTypes = new Set(docsForTypes.map((doc) => doc.document_type));

    return Array.from(uniqueTypes)
      .filter(
        (type): type is DocumentType =>
          type !== null && type !== undefined && !EXCLUDED_DOCUMENT_TYPES.has(type),
      )
      .map((type) => ({
        id: type,
        label: replaceBucketContent(getDocumentTypeLabel(type), deceasedUserData),
      }))
      .sort((a, b) => {
        if (a.id === DocumentType.OTHER) return 1;
        if (b.id === DocumentType.OTHER) return -1;
        return a.label.localeCompare(b.label);
      });
  }, [allDocuments, documents, deceasedUserData]);

  // Reset filter to "all" if the selected type no longer exists in available types
  useEffect(() => {
    if (
      selectedDocumentType !== "all" &&
      !filteredDocumentTypes.some((type) => type.id === selectedDocumentType)
    ) {
      onDocumentTypeChange("all");
    }
  }, [filteredDocumentTypes, selectedDocumentType, onDocumentTypeChange]);

  const handleDocumentTypeChange = (value: string) => {
    // If "Other" is selected, we need to handle it specially
    // For now, we'll pass it through as-is since the parent component
    // will need to handle the filtering logic for "Other" documents
    onDocumentTypeChange(value as DocumentType | "all");
  };

  const handleUploadClick = () => {
    if (onUploadClick) {
      const result = onUploadClick();
      if (result === false) return;
    }

    setIsUploadModalOpen(true);
  };

  const hasSearchOrFilter = _searchQuery !== "" || selectedDocumentType !== "all";

  return (
    <div className="border border-primary w-full bg-white rounded-lg flex flex-col overflow-hidden">
      {activeFolder?.is_auto_generated && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-primary bg-background px-4 sm:px-6 py-3">
          <p className="text-xs font-medium text-secondary">
            Elayne grouped these {activeFolder.file_count} files because they share a type.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => handleDeleteFolder(activeFolder)}
              className="text-xs font-medium text-primary hover:underline"
            >
              Delete folder
            </button>
            <button
              onClick={() => handleRenameFolder(activeFolder)}
              className="text-xs font-medium text-primary hover:underline"
            >
              Rename
            </button>
          </div>
        </div>
      )}
      <div className="p-4 sm:p-6 flex flex-col">
      {uploadError && (
        <div className="mb-4 rounded-lg border border-destructive bg-destructive-light px-4 py-3">
          <p className="text-sm text-destructive">{uploadError}</p>
        </div>
      )}


      <div className="flex-shrink-0 w-full flex flex-col gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <Input
              variant="search"
              placeholder={activeFolder ? "Search in this folder" : "Search documents and folders"}
              value={_searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Select value={selectedDocumentType} onValueChange={handleDocumentTypeChange}>
              <SelectTrigger
                className="w-full min-w-0 sm:w-auto sm:min-w-[160px]"
                aria-label="Filter document type"
              >
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="max-w-[250px]">
                <SelectItem value="all">All Types</SelectItem>
                {filteredDocumentTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id} title={type.label}>
                    <span className="truncate block max-w-[200px]">{type.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="brand"
              size="md"
              className="flex-shrink-0 whitespace-nowrap"
              onClick={handleUploadClick}
              aria-label="Add document"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add Document</span>
              <span className="sm:hidden">Add</span>
            </Button>
            {!activeFolder && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="md" className="flex-shrink-0 h-10 w-10 p-0" aria-label="More options">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[240px] p-2">
                  <DropdownMenuItem onClick={handleNewFolder} className="cursor-pointer h-10 px-3">
                    <FolderPlus className="mr-2 h-4 w-4" />
                    New folder
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadEverything} className="cursor-pointer h-10 px-3">
                    <Download className="mr-2 h-4 w-4" />
                    Download everything
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      <div className="-mx-6 -mb-6">
        <DocumentVaultTable
          documents={documents}
          allDocuments={allDocuments}
          isLoading={isLoading}
          error={error}
          onUploadClick={handleUploadClick}
          documentTypes={filteredDocumentTypes}
          hasSearchOrFilter={hasSearchOrFilter}
          onClearFilters={() => {
            onSearchChange("");
            onDocumentTypeChange("all");
          }}
          folders={hasSearchOrFilter || activeFolder ? [] : folders}
          onFolderOpen={onFolderOpen}
          onFolderRename={handleRenameFolder}
          onFolderDelete={handleDeleteFolder}
          onFolderDownload={handleFolderDownload}
          onNewFolderAndMove={handleNewFolderAndMove}
          insideFolder={!!activeFolder}
          allFolders={folders}
        />
      </div>

      <FolderNameDialog
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        mode={folderDialogMode}
        initialName={renamingFolder?.name}
        existingNames={existingFolderNames}
        onSubmit={handleFolderDialogSubmit}
        isLoading={createFolderMutation.isPending || renameFolderMutation.isPending}
      />

      {person?.estate_id && (
        <>
          <UploadDocumentModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            estateId={person.estate_id}
            size="lg"
            maxFiles={remainingSlots}
            onLimitExceeded={handleLimitExceeded}
            onSuccess={handleUploadSuccess}
            onTakePhoto={() => setIsMobileUploadOpen(true)}
          />
          {limitModalFiles && (
            <UploadLimitModal
              isOpen={!!limitModalFiles}
              onClose={() => setLimitModalFiles(null)}
              accepted={limitModalFiles.accepted}
              blocked={limitModalFiles.blocked}
              onUpgradeAll={() => {
                setLimitModalFiles(null);
                onLimitReached?.();
              }}
              onUploadAccepted={handleUploadAccepted}
              isUploading={isLimitUploading}
            />
          )}
        </>
      )}
      <MobileUploadModal
        isOpen={isMobileUploadOpen}
        onClose={() => setIsMobileUploadOpen(false)}
        onTryAnotherWay={() => {
          setIsMobileUploadOpen(false);
          setIsUploadModalOpen(true);
        }}
        maxUploads={remainingSlots}
      />
      </div>
    </div>
  );
};

export default DocumentVaultList;
